/**
 * useReasoningStream — SSE hook for real-time LLM reasoning display.
 *
 * Parses the standard SSE reasoning protocol events:
 *   event: reasoning   { text, seq, source?, kind? }
 *   event: tool_use    { tool, status, args?, result? }
 *   event: done        { reply, artifacts, reasoning_trace, usage }
 *   event: error       { detail, code? }
 *   event: status      { step }
 *
 * The `kind` field on reasoning events distinguishes:
 *   kind="step"  (default / omitted) — a complete semantic thought; appended
 *                as a numbered entry in reasoningSteps.
 *   kind="delta" — a raw LLM token chunk; accumulated into liveText so the
 *                  panel shows a typewriter-style stream instead of thousands
 *                  of individual single-token rows (the 1371-steps bug).
 *
 * Delta accumulation rules:
 *   - Tokens are concatenated into liveText.
 *   - Every DELTA_FLUSH_CHARS characters (≈ a sentence) the buffer is
 *     promoted to a completed step to keep the panel scrollable.
 *   - A hard newline in the delta stream also triggers a flush.
 *   - Arriving kind="step" events flush any buffered live text first.
 *   - The done event flushes any remaining live text.
 *
 * Works with both EventSource-style SSE and fetch-based streaming.
 *
 * Returns:
 *   reasoningSteps  {Array}    [{seq, text, source, timestamp}]
 *   liveText        {string}   Current reasoning sentence accumulating
 *   toolActions     {Array}    [{tool, status, args, result, timestamp}]
 *   isStreaming      {boolean}  True while SSE connection is active
 *   isDone          {boolean}  True after done event received
 *   donePayload     {Object}   The done event payload (reply, artifacts, etc.)
 *   error           {string}   Error message if error event received
 *   reset           {fn}       Reset state for a new stream
 *
 * Usage:
 *   const { reasoningSteps, liveText, toolActions, isStreaming, reset } =
 *       useReasoningStream();
 *
 *   // In your fetch-based SSE handler:
 *   processSSELine(line);
 */
import { useState, useCallback, useRef } from 'react';

// How many accumulated delta characters trigger an automatic flush to a step.
const DELTA_FLUSH_CHARS = 300;

export const useReasoningStream = () => {
    const [reasoningSteps, setReasoningSteps] = useState([]);
    const [liveText, setLiveText] = useState('');
    const [toolActions, setToolActions] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [donePayload, setDonePayload] = useState(null);
    const [error, setError] = useState('');

    const liveTextRef = useRef('');
    const seqRef = useRef(0);
    const lastStepTextRef = useRef('');

    const reset = useCallback(() => {
        setReasoningSteps([]);
        setLiveText('');
        setToolActions([]);
        setIsStreaming(false);
        setIsDone(false);
        setDonePayload(null);
        setError('');
        liveTextRef.current = '';
        seqRef.current = 0;
        lastStepTextRef.current = '';
    }, []);

    /**
     * _flushLiveText — promote any buffered live delta text to a completed step.
     * Returns the flushed text (or '' if nothing to flush) so callers can skip
     * the setReasoningSteps batching round-trip if the buffer is empty.
     */
    const _flushLiveText = useCallback((source = 'llm') => {
        const buffered = liveTextRef.current.trim();
        if (!buffered) return;
        seqRef.current += 1;
        const flushedStep = {
            seq: seqRef.current,
            text: buffered,
            source,
            timestamp: Date.now(),
        };
        setReasoningSteps(prev => [...prev, flushedStep]);
        liveTextRef.current = '';
        setLiveText('');
    }, []);

    const handleReasoningEvent = useCallback((data) => {
        setIsStreaming(true);
        const { text, seq, source, kind } = data;
        const stepText = text || '';

        // ── Delta token: accumulate into live text ───────────────────────────
        if (kind === 'delta') {
            const next = liveTextRef.current + stepText;
            liveTextRef.current = next;
            setLiveText(next);

            // Auto-flush to a step on hard newlines or when the buffer is long
            // enough to be a readable sentence.
            if (next.includes('\n') || next.length >= DELTA_FLUSH_CHARS) {
                _flushLiveText(source || 'llm');
            }
            return;
        }

        // ── Completed step: flush any live text first, then add this step ────
        _flushLiveText(source || 'llm');

        // Use seq-based dedup when the server sends sequence numbers.
        // Fall back to text-equality dedup only for high-level routing
        // messages (no seq), not for streamed LLM token chunks.
        const incomingSeq = seq != null ? seq : null;
        if (incomingSeq != null && incomingSeq <= seqRef.current) return;
        if (incomingSeq == null && stepText === lastStepTextRef.current) return;

        lastStepTextRef.current = stepText;
        seqRef.current = incomingSeq != null ? incomingSeq : seqRef.current + 1;

        const step = {
            seq: seqRef.current,
            text: stepText,
            source: source || 'status',
            timestamp: Date.now(),
        };

        setReasoningSteps(prev => [...prev, step]);
        // liveText intentionally cleared by _flushLiveText above
    }, [_flushLiveText]);

    const handleToolUseEvent = useCallback((data) => {
        const { tool, status, args, result } = data;
        const action = {
            tool,
            status,
            args: args || null,
            result: result || null,
            timestamp: Date.now(),
        };

        setToolActions(prev => {
            if (status === 'started') {
                return [...prev, action];
            }
            // Update existing action for completed/failed
            const idx = prev.findIndex(
                a => a.tool === tool && a.status === 'started'
            );
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = { ...updated[idx], ...action };
                return updated;
            }
            return [...prev, action];
        });
    }, []);

    const handleDoneEvent = useCallback((data) => {
        // Flush any remaining delta tokens as a final completed step
        _flushLiveText('llm');
        setIsStreaming(false);
        setIsDone(true);
        setDonePayload(data);
        setLiveText('');
        liveTextRef.current = '';
    }, [_flushLiveText]);

    const handleErrorEvent = useCallback((data) => {
        setIsStreaming(false);
        setError(data.detail || 'Unknown error');
    }, []);

    /**
     * processSSELine — parse a raw SSE line pair (event + data).
     *
     * Call this from your fetch-based streaming loop.
     * Expects either:
     *   - A full SSE block: "event: reasoning\ndata: {...}"
     *   - Just the data line if event type is tracked separately
     */
    const processSSELine = useCallback((eventType, dataStr) => {
        let data;
        try {
            data = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
        } catch {
            return;
        }

        switch (eventType) {
            case 'reasoning':
                handleReasoningEvent(data);
                break;
            case 'tool_use':
                // Tool actions flush any live delta text so the step list stays
                // readable even while the LLM is mid-sentence.
                _flushLiveText('llm');
                handleToolUseEvent(data);
                break;
            case 'done':
                handleDoneEvent(data);
                break;
            case 'error':
                handleErrorEvent(data);
                break;
            case 'status':
            case 'thinking':
                // Treat legacy thinking/status events as completed steps
                if (data.step || data.text) {
                    handleReasoningEvent({
                        text: data.step || data.text,
                        source: 'status',
                        kind: 'step',
                    });
                }
                break;
            default:
                break;
        }
    }, [handleReasoningEvent, handleToolUseEvent, handleDoneEvent, handleErrorEvent, _flushLiveText]);

    /**
     * parseSSEChunk — parse a raw SSE text chunk that may contain
     * multiple "event: ...\ndata: ...\n\n" blocks.
     *
     * Useful when reading from a ReadableStream.
     */
    const parseSSEChunk = useCallback((chunk) => {
        const lines = chunk.split('\n');
        let currentEvent = '';

        for (const line of lines) {
            if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
                const dataStr = line.slice(6);
                if (currentEvent) {
                    processSSELine(currentEvent, dataStr);
                    currentEvent = '';
                }
            }
        }
    }, [processSSELine]);

    return {
        reasoningSteps,
        liveText,
        toolActions,
        isStreaming,
        isDone,
        donePayload,
        error,
        reset,
        processSSELine,
        parseSSEChunk,
    };
};

export default useReasoningStream;
